import { useMemo, useState } from "react";

const NO_WHEN_PREFIX = /^(every|the day|on the|day of|\d+ days?|when\b)/i;
const NO_IF_PREFIX = /^(if|unless|except|excluding|but not|for|shot by)\b/i;

export function composeText({ when, cond, then }) {
  const w = when.trim().replace(/,$/, "");
  const c = cond.trim().replace(/,$/, "");
  const parts = [NO_WHEN_PREFIX.test(w) ? w : `When ${w}`];
  if (c) parts.push(NO_IF_PREFIX.test(c) ? c : `if ${c}`);
  parts.push(then.trim());
  const s = parts.filter(Boolean).join(", ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function splitText(text) {
  const t = (text || "").trim();
  const thenM = t.match(/,?\s*(?:then\s+)?\b(assign|set (?:it|them)|mark|remind|email|notify|text|send|post|create)\b/i);
  const head = thenM ? t.slice(0, thenM.index) : t;
  const then = thenM ? t.slice(thenM.index).replace(/^,?\s*(then\s+)?/i, "") : "";
  const ifM = head.match(/,?\s*\b(if|unless|except|excluding|but not|shot by)\b/i);
  const when = (ifM ? head.slice(0, ifM.index) : head).replace(/^when\s+/i, "").replace(/,\s*$/, "");
  const cond = ifM ? head.slice(ifM.index).replace(/^,?\s*(if\s+)?/i, "").replace(/,\s*$/, "") : "";
  return { when, cond, then };
}

export function buildSuggestions(v) {
  const P = v.products || [], C = v.clients || [], Ph = v.photographers || [], T = v.team || [], S = v.statuses || [];
  return {
    when: [
      "a new order is booked", "a shoot is booked", "a shoot is rescheduled", "the day before a shoot", "the day of a shoot",
      "2 days before a shoot", ...Ph.map((p) => `a shoot is booked for ${p}`), ...Ph.map((p) => `a shoot is rescheduled for ${p}`),
      ...Ph.map((p) => `the day before a shoot for ${p}`), ...P.map((p) => `a ${p} product is added`), ...P.map((p) => `${p} is due tomorrow`),
      ...P.map((p) => `${p} is due today`), "a product is due tomorrow", "a product is due in 3 days",
      "every weekday at 7am", "every day at 8am", "every Monday at 8am",
    ],
    cond: [
      ...Ph.map((p) => `photographer is ${p}`), ...Ph.map((p) => `not photographer ${p}`),
      ...C.map((c) => `for ${c}`), ...C.map((c) => `except for ${c}`),
      ...P.map((p) => `not ${p}`), "unassigned", "not assigned",
      ...S.map((s) => `status is ${s}`), ...S.map((s) => `unless it's ${s}`),
    ],
    then: [
      "remind me", "send the email", "email me", "email the photographer", "assign it to the photographer",
      ...T.map((t) => `assign it to ${t}`), ...T.map((t) => `email ${t}`),
      ...S.map((s) => `set it to ${s}`),
    ],
  };
}

export function SmartField({ label, hint, value, onChange, options, placeholder, testid }) {
  const [focus, setFocus] = useState(false);
  const lastCut = Math.max(value.lastIndexOf(","), value.toLowerCase().lastIndexOf(" and "), value.toLowerCase().lastIndexOf(" or "));
  const frag = (lastCut >= 0 ? value.slice(lastCut).replace(/^(,| and | or )/i, "") : value).trim().toLowerCase();
  const list = useMemo(() => {
    const words = frag.split(/\s+/).filter(Boolean);
    const rank = (o) => { const l = o.toLowerCase(); return l === frag ? 0 : l.startsWith(frag) && (l.length === frag.length || /\s/.test(l[frag.length])) ? 1 : l.startsWith(frag) ? 2 : 3; };
    return options.filter((o) => words.every((w) => o.toLowerCase().includes(w)))
      .map((o, i) => [o, rank(o), i]).sort((a, b) => a[1] - b[1] || a[2] - b[2]).map((x) => x[0]).slice(0, 8);
  }, [frag, options]);
  const pick = (o) => {
    const base = lastCut >= 0 ? value.slice(0, lastCut).trimEnd() : "";
    const sep = lastCut >= 0 ? value.slice(lastCut).match(/^(,| and | or )/i)[0].trim() : "";
    onChange(base ? `${base}${sep === "," ? "," : ` ${sep}`} ${o}` : o);
  };
  return (
    <div className="relative" data-testid={`${testid}-wrap`}>
      <div className="mb-1 flex items-baseline gap-2">
        <span className="font-mono text-[11px] uppercase tracking-wider text-primary">{label}</span>
        <span className="text-[11px] text-muted-foreground">{hint}</span>
      </div>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 150)}
        className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm focus:border-primary focus:outline-none" data-testid={testid} />
      {focus && list.length > 0 && !(list.length === 1 && list[0].toLowerCase() === frag) && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg" data-testid={`${testid}-suggestions`}>
          {list.map((o, i) => (
            <button key={o} type="button" onMouseDown={(e) => { e.preventDefault(); pick(o); }} data-testid={`${testid}-suggestion-${i}`}
              className="block w-full rounded px-2.5 py-1.5 text-left text-sm text-foreground/90 transition-colors hover:bg-secondary">{o}</button>
          ))}
        </div>
      )}
    </div>
  );
}
